import { Link } from 'react-router-dom';

interface ComicCardProps {
  id: number;
  title: string;
  author: string;
  rating: number;
  status: string;
  updateChapter: number;
  totalChapters: number | null;
  coverImage: string;
  coverImageLarge: string;
  description?: string;
  tags: string[];
  views: number;
  variant?: 'default' | 'featured' | 'compact';
}

function formatViews(n: number): string {
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}千万`;
  if (n >= 10000) return `${(n / 10000).toFixed(0)}万`;
  return n.toString();
}

export default function ComicCard({
  id,
  title,
  author,
  rating,
  status,
  updateChapter,
  totalChapters,
  coverImage,
  coverImageLarge,
  description,
  tags,
  views,
  variant = 'default',
}: ComicCardProps) {
  const chapterLabel = totalChapters
    ? `全${totalChapters}话`
    : `更新至${updateChapter}话`;

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${id}`}
        className="group block relative rounded-lg overflow-hidden bg-foreground-900 h-full cursor-pointer"
        data-product-shop=""
      >
        <div className="absolute inset-0">
          <img
            src={coverImageLarge}
            alt={title}
            title={`${title} - 韩漫大全`}
            className="w-full h-full object-cover object-top opacity-60 group-hover:opacity-50 transition-opacity duration-300"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent"></div>
        <div className="relative z-10 p-6 flex flex-col justify-end h-full">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              status === '连载中' ? 'bg-accent-500/90 text-background-50' : 'bg-secondary-500/90 text-background-50'
            }`}>
              {status}
            </span>
            <span className="text-xs text-background-300">{chapterLabel}</span>
          </div>
          <h3 className="text-xl font-heading text-background-50 mb-1">{title}</h3>
          <p className="text-xs text-background-400 mb-3">{author} · {rating}分</p>
          {description && (
            <p className="text-sm text-background-300 line-clamp-2 mb-4">{description}</p>
          )}
          <span className="inline-flex items-center gap-1 text-sm text-accent-400 group-hover:text-accent-300 transition-colors whitespace-nowrap">
            立即阅读 <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${id}`}
        className="group flex gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
        data-product-shop=""
      >
        <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={coverImage}
            alt={title}
            title={`${title} - 韩漫大全`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{title}</h4>
          <p className="text-xs text-foreground-500 mt-1">{author}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-accent-500 font-medium">{rating}分</span>
            <span className="text-xs text-foreground-400">{chapterLabel}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      data-product-shop=""
    >
      <div className="aspect-[3/4] overflow-hidden bg-background-100 relative">
        <img
          src={coverImage}
          alt={title}
          title={`${title} - 韩漫大全`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            status === '连载中' ? 'bg-accent-500 text-background-50' : 'bg-secondary-500 text-background-50'
          }`}>
            {status}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/70 to-transparent p-3">
          <div className="flex items-center gap-1 text-background-50 text-xs">
            <i className="ri-eye-line"></i>
            <span>{formatViews(views)}</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{title}</h3>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-foreground-500">{author}</span>
          <span className="text-xs text-accent-500 font-medium">{rating}分</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-foreground-400">{chapterLabel}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}